<template>
<div>
<widget
	:title="entryTitle"
	:header-button="headerButtonIcon"
	:hide-body="collapsed"
	class="form"
	v-on:headerButtonClick="headerButtonClick">

	<template slot="header">
		<div class="additional-header-buttons">
			<i v-if="!readonly && !editMode" class="fa icon-button" :class="otherHeaderButtonIcon" v-on:click="editEntry"></i>
		</div>
	</template>

	<template v-if="collapsed">
		<span v-if="localEntry.customer_asset_id > 0" class="text-muted">For asset {{selectedAssetText}}</span>
	</template>
	<template v-else>
		<form>

			<slot></slot>

			<template>
				<div v-if="'WorkCompletedBy' in localEntry && 'created_at' in localEntry && localEntry.WorkCompletedBy" class="text-muted">
					Entered by {{localEntry.WorkCompletedBy.first_name}}  {{localEntry.WorkCompletedBy.last_name}} on {{formatDateTimeToLocal(localEntry.created_at)}}
				</div>

				<div v-if="!disableAssets && !('customer_asset_id' in localEntry) && !('id' in localEntry) && workedOnAsset == null" class="text-center">
					<h5>Did you work on a customer's asset/piece of equipment?</h5>
					<field
						:params="{type:'btn-group',buttons:[
							{text:'No',value:0},
							{text: 'Yes',value:1}
						]}"
						v-model="workedOnAsset">
					</field>
				</div>

				<template v-else>

					<field v-if="editMode && !disableAssets"
						label="I worked on a customer asset"
						col-class="col-sm-12"
						:params="{type:'boolean'}"
						:readonly="readonly || !editMode"
						v-model="workedOnAsset"
						:control-left="true">
					</field>

					<form-group v-if="editMode && workedOnAsset && !disableAssets" label="Asset Worked On" col-class="col-md-2 col-sm-3">

						<div class="d-flex flex-wrap align-items-center mb-2">
							<field class="flex-grow-1"
								:params="{type:'select',options:customerAssetOptions}"
								:readonly="readonly || !editMode"
								v-model="localEntry.customer_asset_id"
								v-on:change="selectedAssetChange">

								<div v-if="!localEntry.customer_asset_id" class="alert alert-info mt-1">
									<div class="text-muted">
										<i class="fa fa-exclamation mr-2"></i>
										Can't find the asset you're looking for? Make sure the Service Work Location is set to a Customer Location, and make sure thet asset you're looking for is assigned to that location.
									</div>
								</div>

								<div v-if="selectedAsset" class="mt-1 mb-3">

									<div v-if="selectedAsset.Checklist.length" class="alert alert-info mb-1">
										<div class="text-muted d-flex justify-content-between align-items-baseline">
											<div>
												<i class="fa fa-exclamation mr-2"></i>
												This asset has {{selectedAsset.Checklist.length}} checklists that can be imported into this service
											</div>
											<a v-if="!assetChecklistsImported" href="#" v-on:click.prevent="importAssetChecklists">Import now</a>
											<span v-else>Imported!</span>
										</div>
									</div>

									<div v-if="selectedAsset.FormChildTemplate.length" class="alert alert-info mb-1">
										<div class="text-muted d-flex justify-content-between align-items-baseline">
											<div>
												<i class="fa fa-exclamation mr-2"></i>
												This asset has {{selectedAsset.FormChildTemplate.length}} form child templates that can be imported into this service
											</div>
											<a v-if="!assetFormsImported" href="#" v-on:click.prevent="importAssetForms">Import now</a>
											<span v-else>Imported!</span>
										</div>
									</div>
								</div>

							</field>

							<button v-if="editMode && (!('customer_asset_id' in localEntry) || localEntry.customer_asset_id == 0)"
								class="btn btn-dark" v-on:click="createAsset" type="button">
								<i class="fa fa-tractor"></i>
								<i class="fa fa-plus"></i>
								Create Customer Asset
							</button>

							<a v-if="filteringToLocationAssets" href="#" v-on:click.prevent="toggleAssetFilter" class="ml-2">See all assets</a>
							<a v-else href="#" v-on:click.prevent="toggleAssetFilter" class="ml-2">Show only location's assets</a>
						</div>
					</form-group>

					<field v-if="localEntry.customer_asset_id > 0 && workedOnAsset"
						label="Work Type"
						col-class="col-md-2 col-sm-3"
						:params="{type:'select',options:workTypeOptions}"
						:readonly="readonly || !editMode"
						v-model="localEntry.work_type">
					</field>

					<field v-if="hasChangeQuoteRecord && quoteRecord.quote_status == 'Accepted'"
						label="This work belongs to the change quote"
						col-class="col-sm-12"
						:params="{type:'boolean'}"
						:readonly="readonly || !editMode"
						v-model="localEntry.is_change_quote_work"
						:control-left="true">
					</field>

					<form-group label="Work Completed" col-class="col-md-2 col-sm-3">

						<div v-if="!readonly && editMode">
							<div>
								Tell us what you did. <strong class="ml-1">Remember:</strong>
								
								<ol class="mt-2">
									<li>Use bullet points in your descriptions</li>
									<li>Be specific in describing what you did</li>
									<li>To initate auto-save feature, you must first press the SAVE button</li>
								</ol>
							</div>

							<field 
								:params="{type:'richtext',height:'250px'}" 
								v-model="localEntry.work_completed_description" 
								v-on:input="workCompletedChange">
							</field>
						</div>
						<field 
							v-else
							:params="{type:'richtext',height:'250px'}" 
							v-model="localEntry.work_completed_description" 
							:readonly="true">
						</field>
						
					</form-group>

					<form-group v-if="editMode" 
						class="expanded-add-buttons"
						label="Add items" 
						col-class="col-md-2 col-sm-3">
						<div class="d-flex flex-row flex-wrap">

							<template v-if="!readonly" >
								<div v-if="parentIdKey == 'service_id' && 'ServiceContract' in $store.service && $store.service.ServiceContract" class="mr-1">
									<button type="button" class="btn btn-dark mb-2" v-on:click="newTask">
										<i class="fa fa-list mr-1"></i>
										<i class="fa fa-plus"></i>
										
										Item from Schedule of Values
									</button>
								</div>
								<div class="mr-1">
									<button class="btn btn-dark mb-2" v-on:click="newServiceTask" type="button">
										
										<i class="fa fa-hammer mr-1"></i>
										<i class="fa fa-plus"></i>
										Task
									</button>
								</div>
							
								<div class="mr-1">
									<button type="button" class="btn btn-dark mb-2" v-on:click="addMaterialsUsed">
										<i class="fa fa-tags mr-1"></i>
										<i class="fa fa-plus"></i>
										Materials
									</button>
								</div>
								<div class="mr-1">
									<button class="btn btn-dark mb-2" v-on:click="newEquipment" type="button">
										
										<i class="fa fa-truck mr-1"></i>
										<i class="fa fa-plus"></i>
										Equipment
									</button>
								</div>
								<div class="mr-1">
									<button class="btn btn-dark mb-2" v-on:click="newFile" type="button">
										
										<i class="fa fa-image mr-1"></i>
										<i class="fa fa-plus"></i>
										File
									</button>
								</div>
								<div v-if="parentIdKey == 'service_id'" class="mr-1">
									<button class="btn btn-dark mb-2" v-on:click="newForm()" type="button">
										<i class="fa fa-clipboard-check mr-1"></i>
										<i class="fa fa-plus"></i>
										Form
									</button>
								</div>
							</template>
						</div>
					</form-group>

					<button v-if="!readonly" class="btn btn-light float-right text-left collapsed-add-buttons mb-2" type="button">
						<context-menu menu-label="Add Items..."
							:menu-items="addItemsMenuItems"
							v-on:newTask="newTask"
							v-on:newMaterial="newMaterial"
							v-on:newEquipment="newEquipment"
							v-on:newFile="newFile"
							v-on:newForm="newForm()"
							v-on:newServiceTask="newServiceTask"
							v-on:createAsset="createAsset">
						</context-menu>
					</button>
					<div style="clear: both;"></div>
				</template>
			</template>

		</form>

		<template 
			v-if="'ServiceContract' in $store.service && $store.service.ServiceContract && tasksDone.length > 0">

			<hr>
			<div class="d-flex flex-row justify-content-between">
				<h3><i class="fa fa-list"></i> Work done from Schedule of Values</h3>

				<button v-if="!readonly && editMode" type="button" class="btn btn-success mb-2" v-on:click="newTask">
					<i class="fa fa-plus"></i>
					Add
				</button>
			</div>

			<table-list
				record-name="Task"
				:fields="taskFields"
				:hide-record-count="true"
				:records="tasksDone"
				:has-edit="!readonly && editMode"
				:simple-delete="true"
				:has-delete="!readonly && editMode"
				:sortable="true"
				v-on:delete="removeTask"
				v-on:edit="editTask($event,'contractTask')">
			</table-list>
		</template>

		<template 
			v-if="serviceTasksAdded.length > 0">

			<hr>
			<div class="d-flex flex-row justify-content-between">
				<h3><i class="fa fa-hammer"></i> Service Tasks Added</h3>

				<button v-if="!readonly && editMode" type="button" class="btn btn-success mb-2" v-on:click="newServiceTask">
					<i class="fa fa-plus"></i>
					Add
				</button>
			</div>

			<table-list
				record-name="Service Task"
				:fields="taskFields"
				:hide-record-count="true"
				:records="serviceTasksAdded"
				:has-edit="!readonly && editMode"
				:simple-delete="true"
				:has-delete="!readonly && editMode"
				:sortable="true"
				v-on:delete="removeServiceTask"
				v-on:edit="editTask($event,'serviceTask')">
			</table-list>
		</template>

		<modal-dialog
			:modal-visible="taskModal.visible"
			:title="taskModal.title"
			:confirm-button-text="taskModal.buttonText"
			:submit-visible="true"
			modal-size="modal-lg"
			v-on:confirm="addTask"
			v-on:closeModal="taskModal.visible = false">

			<field v-if="serviceQuote && quoteTasks.length > 0"
				class="mb-2"
				:params="{type:'btn-group',buttons:[
					{text: 'Pick from Quote',value:true},
					{text: 'Pick from Schedule of Values',value: false}
				]}"
				v-model="taskModal.pickFromQuote">		
			</field>

			<div v-if="!taskModal.pickFromQuote">
				<div v-for="(task,index) in taskModal.tasksPayload">
					<hr v-if="index > 0">
					<div class="row">
						<div class="col-sm-7">
							<field v-if="taskModal.taskType == 'Service Task'"
								label="Task" 
								col-class="col-sm-3" 
								:params="{type:'select', options:serviceTaskOptions}" 
								v-model="task.task_id"
								v-on:change="taskAddRow">		
							</field>
							<field v-else
								label="Task" 
								col-class="col-sm-3" 
								:params="{type:'select', options:taskOptions}" 
								v-model="task.task_id"
								v-on:change="taskAddRow">		
							</field>
						</div>
						<div class="col-sm-5">
							<field label="Qty" 
								col-class="col-sm-5" 
								:params="{type:'number', min:0,step:0.25}" 
								v-model="task.qty">		
							</field>
						</div>
					</div>
				</div>
			</div>
			<div v-else>
				
				<table class="table table-sm">
					<thead>
						<tr>
							<th></th>
							<th>Item #</th>
							<th>Description</th>
							<th class="text-right">Quoted Qty</th>
							<th class="text-right">Qty</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="task in quoteTasks">
							<td>
								<input type="checkbox" v-model="task.selected">
							</td>
							<td v-on:click="toggleTask(task)">
								{{ task.fieldValues.item_number }}
							</td>
							<td  v-on:click="toggleTask(task)">{{ task.fieldValues.description }}</td>
							<td class="text-right">{{ task.fieldValues.qty }}</td>
							<td class="text-right">
								<field 
									style="width: 100px;float: right;"
									:params="{type:'number',min:0,step:1,align:'right'}" 
									v-model="task.qty">
								</field>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

		</modal-dialog>

		<template v-if="materialsUsed.length > 0">
			
			<hr>

			<div class="d-flex flex-row justify-content-between">
				<h3><i class="fa fa-tags"></i> Materials Used</h3>

				<button v-if="!readonly && editMode" type="button" class="btn btn-success" v-on:click="addMaterialsUsed">
					<i class="fa fa-plus"></i>
					Add
				</button>
			</div>

			<table-list
				:records="materialsUsed"
				:fields="worksheetPartsFields"
				:has-delete="!readonly && editMode"
				:has-edit="!readonly && editMode"
				:simple-delete="true"
				:sortable="true"
				:hide-record-count="true"
				record-name="Material"
				:other-buttons="!readonly && editMode ? [
					{
						text: 'Serial #',
						class: 'btn-light',
						click: 'addSerialNum',
						title: 'Add serial # of material',
						icon: 'fa-plus',
						condition: ['material_unit_type','each']
					},
					{
						text: 'Warranty Date',
						class: 'btn-light',
						click: 'addWarrantyExpiry',
						title: 'Add warranty date of material',
						icon: 'fa-plus',
						condition: ['material_unit_type','each']
					}
				] : false"
				v-on:edit="editMaterialQty"
				v-on:delete="removeMaterial"
				v-on:addSerialNum="addMaterialSerialNum"
				v-on:addWarrantyExpiry="addMaterialWarrantyExpiry">
			</table-list>

		</template>

		<template v-if="equipmentUsed.length > 0">
			
			<hr>
			<div class="d-flex flex-row justify-content-between">
				<h3><i class="fa fa-truck"></i> Equipment Used</h3>

				<button v-if="!readonly && editMode" class="btn btn-success mb-2" v-on:click="newEquipment" type="button">
					<i class="fa fa-plus"></i>
					Add
				</button>
			</div>

			<table-list
				record-name="Equipment Item"
				:fields="equipmentFields"
				:hide-record-count="true"
				:records="mutatedEquipmentUsed"
				:has-edit="!readonly && editMode"
				:simple-delete="true"
				:has-delete="!readonly && editMode"
				:sortable="true"
				v-on:delete="removeEquipment"
				v-on:edit="editEquipment">
			</table-list>

		</template>

		<template v-if="forms.length > 0">
			<hr>

			<h3>Forms</h3>

			<table-list record-name="Form"
				:fields="[
					{label: 'Template', key: 'breadcrumb',showOnMobile:false},
					{label: 'Description',key:'childTemplateDescription',showOnMobile:false},
					{label: 'Completed At',key:'completedAt',showOnMobile:false},
				]"
				:records="mutatedForms"
				:hide-record-count="true"
				:sortable="true"
				has-edit="/form/view/"
				:edit-new-tab="true"
				edit-button-text="View"
				edit-button-icon="fa-eye"
				id-field="slug"
				:mobile-breakpoint="500">
			</table-list>
		</template>

		<modal-dialog
			:modal-visible="equipmentModal.visible"
			:title="equipmentModal.title"
			:confirm-button-text="equipmentModal.buttonText"
			:submit-visible="true"
			modal-size="modal-lg"
			v-on:confirm="addEquipment"
			v-on:closeModal="equipmentModal.visible = false">
		
			<form-group label="Equipment" col-class="col-4" v-if="equipmentModal.visible">
				<label>
					<input type="checkbox" value="true" v-model="equipmentModal.manualEntry">
					Enter Equipment Manually...
				</label>
				<equipment-picker v-if="!equipmentModal.manualEntry && $store.equipments.length > 0"
					v-model="equipmentModal.payload.equipment_id"
					v-on:equipmentChange="setSelectedEquipment">
				</equipment-picker>
				<field v-else 
					v-model="equipmentModal.payload.manualEquipment"
					:params="{type:'text',placeholder: 'Describe the equipment you used'}">
				</field>
				<p v-if="$store.equipments.length == 0">You have no equipment set up in your equipment database</p>
			</form-group>

			<field v-if="equipmentModal.payload.equipment_id > 0 || equipmentModal.payload.manualEquipment"
				label="Hours"
				col-class="col-4" 
				:params="{type:'number',step:0.25,min:0}" 
				v-model="equipmentModal.payload.qty">
			</field>

		</modal-dialog>

		<template v-if="localEntry.customer_asset_id > 0">
			<field
				label="Next Service Due"
				col-class="col-md-3 col-sm-3"
				:params="{type:'date'}"
				:readonly="readonly || !editMode"
				v-model="localEntry.nextServiceDueDate">
			</field>

			<field
				label="Next Service Description"
				col-class="col-md-3 col-sm-3"
				:params="{type:'text'}"
				:readonly="readonly || !editMode"
				v-model="localEntry.nextServiceDescription">
			</field>

		</template>

		<div v-if='editMode && !readonly' class="d-flex flex-row justify-content-between">
			<div>
				<button type="button" class="btn btn-outline-danger" v-on:click="deleteEntry">
					<i class="fa fa-trash"></i>
					Delete Entry
				</button>
			</div>
			<div>
				<button type="button" class="btn btn-success" v-on:click="saveEntry">
					<i class="fa fa-save"></i>
					Save
				</button>
			</div>
		</div>
	</template>

</widget>

<job-file-upload-modal :visible="uploadModal.visible"
	title="Add a File"
	:record="uploadModal.payload"
	:job-id="parentIdKey == 'service_id' ? parentId : $store.project.id"
	:is-edit="false"
	:is-for-projects="false"
	:store-location="parentIdKey == 'service_id' ? 'service' : 'project'"
	:store-location-map="{
		service: {
			apiResource: 'ServiceFile',
			jobField: parentIdKey,
			tagFilterField: 'is_for_services',
			recordTypeLabel: 'Service'
		},
		project: {
			apiResource: 'JobFile',
			jobField: parentIdKey,
			tagFilterField: 'is_for_projects',
			recordTypeLabel: 'Project'
		}
	}"
	v-on:modelChanged="fileSaved"
	v-on:closeModal="uploadModal.visible = false">

	<p>Files uploaded here will land in the 
		<router-link v-if="parentIdKey == 'service_id'" :to="'/service/'+$store.service.slug+'/files'">Files</router-link> 
		<router-link :to="'/service/' + $store.project.slug + '/files'">Files</router-link> 
	tab</p>

</job-file-upload-modal>

<modal-dialog
	:modal-visible="addAssetModal.visible"
	title="Add Customer Asset"
	confirm-button-text="Save"
	:working="addAssetModal.working"
	v-on:closeModal="addAssetModal.visible = false"
	v-on:confirm="saveNewAsset">

	<field label="Category"
		col-class="col-md-4"
		:params="{type:'select',options:customerAssetCategoryOptions}"
		v-model="addAssetModal.payload.category_id">
	</field>

	<field label="Description"
		col-class="col-md-4"
		:params="{type:'text'}"
		v-model="addAssetModal.payload.description">
	</field>

	<field label="Serial Number"
		col-class="col-md-4"
		:params="{type:'text'}"
		v-model="addAssetModal.payload.serial_num">
	</field>

	<field label="Date Acquired"
		col-class="col-md-4"
		:params="{type:'date'}"
		v-model="addAssetModal.payload.date_acquired">
	</field>

</modal-dialog>

<modal-dialog
	:modal-visible="deleteEntryModalVisible"
	title="Delete Work Completed Entry"
	confirm-button-text="Yes, Delete"
	button-class="btn-outline-danger"
	:submit-visible="true"
	v-on:confirm="confirmDeleteEntry"
	v-on:closeModal="deleteEntryModalVisible = false">

	Are you sure you want to delete this entry?

</modal-dialog>

<modal-dialog
	:modal-visible="materialFieldEditModal.visible"
	title="Edit Material"
	confirm-button-text="Save"
	v-on:confirm="saveMaterialEdit"
	v-on:closeModal="materialFieldEditModal.visible = false">

	<table-list
		:fields="[
			{key: 'fieldLabel', label: ''},
			{key: 'fieldValue', label: ''}
		]"
		:records="editedMaterialFieldValues"
		:hide-record-count="true">
	</table-list>

	<field :label="materialFieldEditModal.fieldLabel"
		col-class="col-auto"
		v-model="materialFieldEditModal.fieldValue"
		:params="{type: materialFieldEditModal.fieldType}">
	</field>

</modal-dialog>

<material-selector-modal 
	:visible="materialModalVisible"
	:expense-log-prompt="true"
	:contract-id="$store.service.contract_id"
	:suggested-materials="suggestedMaterials"
	:disable-assemblies="false"
	:service-id="parentId"
	v-on:closeModal="materialModalVisible = false"
	v-on:addMaterials="addMaterials">
</material-selector-modal>

</div>
</template>
<script type="text/javascript">
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import ModalDialog from "@/components/ModalDialog"
import EquipmentPicker from "@/components/equipment_picker/EquipmentPicker"
import MaterialSelectorModal from "@/components/super_components/MaterialSelectorModal"
import Widget from "@/components/Widget"
import TableList from "@/components/TableList"
import ContextMenu from "@/layout_fragments/ContextMenu"

import JobFileUploadModal from "@/components/super_components/JobFileUploadModal"

import ImageThumbList from "@/components/ImageThumbList"
import UploadFieldWithPanel from "@/components/super_components/UploadFieldWithPanel"

import ServiceMixins from "@/mixins/ServiceMixins.js"

export default {
	name: "WorkCompletedEntry",
	components: {
		Field,
		FormGroup,
		ModalDialog,
		EquipmentPicker,
		MaterialSelectorModal,
		Widget,
		TableList,
		ImageThumbList,
		UploadFieldWithPanel,
		JobFileUploadModal,
		ContextMenu
	},
	mixins: [ServiceMixins],
	props: [
		"entry",
		"parentId",
		"parentIdKey",
		"equipmentUsedFields",
		"suggestedMaterials",
		"readonly",
		"taskOptions",
		"equipmentList",
		"triggerEditMode",
		"worksheetApiResource",
		"disableAssets"
	],
	data() {
		return {
			collapsed: true,
			customerAssets: [],
			localEntry: {},
			materialModalVisible: false,
			tasksDone: [],
			serviceTasksAdded: [],
			equipmentUsed: [],
			materialsUsed: [],
			quoteTasks: [],
			forms: [],
			tasksFetched: false,
			customerAssetCategories: [],
			equipmentModal: {
				visible: false,
				payload: {},
				title: "",
				buttonText: "",
				selectedEquipment: null,
				manualEntry: false
			},
			materialFieldEditModal: {
				visible: false,
				fieldLabel: "",
				fieldType: "",
				fieldKey: "",
				fieldValue: null,
				record: null
			},
			taskModal: {
				visible: false,
				tasksPayload: [],
				title: "",
				buttonText: "Add",
				new: true,
				pickFromQuote: null
			},
			uploadModal: {
				visible: false,
				tags: [],
				payload: {
					caption: ""
				},
				file_id: null
			},
			addAssetModal: {
				visible: false,
				payload: {},
				working:false
			},
			editMode: false,
			deleteEntryModalVisible: false,
			workedOnAsset: null,
			workTypeOptions: [
				{text: "Inspection",value:"Inspection"},
				{text: "Preventative Maintenance",value:"Preventative Maintenance"},
				{text: "Repair",value:"Repair"},
				{text: "Replacement",value:"Replacement"},
				{text: 'Install',value: 'Install'},
				{text: "Other",value:"Other"},
			],
			filteringToLocationAssets: true,
			assetChecklistsImported: false,
			assetFormsImported: false,
			worksheetPartsFields: [
				{label: "Part Description",key:"description"},
				{label: "Part #",key:"part_number"},
				{label: "Qty",key:"qty",align:"right"},
				{label: "",key:'material_unit_type'},
				{label: "Serial #",key:'serialNum'},
				{label: "Warranty Expiry",key:'warrantyExpiry'},
			],
		}
	},
	watch: {
		triggerEditMode() {
			this.editMode = true
			this.collapsed = false
		},
		entry() {
			this.setLocalEntry()
		}
	},
	computed: {
		selectedAssetText() {
			let asset = this.customerAssetOptions.find(opt => opt.value == this.localEntry.customer_asset_id)
			if (asset)
				return asset.text

			return ""
		},
		selectedAsset() {
			return this.customerAssets.find(asset => asset.id == this.localEntry.customer_asset_id)
		},
		addItemsMenuItems() {
			let items = []

			if ('ServiceContract' in this.$store.service && this.$store.service.ServiceContract) {
				items.push({
					icon: "fa-plus",
					text: 'Item from Schedule of Values',
					clickFunction: "newTask"
				})
			}

			items.push({
				icon: 'fa-hammer',
				text: 'Task',
				clickFunction: 'newServiceTask'
			})

			items.push({
				icon: "fa-plus",
				text: 'Materials',
				clickFunction: "newMaterial"
			})

			items.push({
				icon: "fa-plus",
				text: 'Equipment',
				clickFunction: "newEquipment"
			})

			items.push({
				icon: "fa-plus",
				text: 'File',
				clickFunction: "newFile"
			})

			return items
		},
		fileTagOptions() {
			return this.optionify(this.$store.fileTags,"name")
		},
		serviceTaskOptions() {
			if (this.$store.serviceTasks) {
				return this.optionify(this.$store.serviceTasks.map(task => {
					task.displayField = task.item_number + ": "+ task.description
					return task
				}),"displayField")
			}
		},
		customerAssetOptions() {
			return this.optionify(this.customerAssets.map(asset => {
				let serial_num = ""
				if (asset.serial_num)
					serial_num = " (S/N "+asset.serial_num+")"

				asset.display = asset.description+serial_num
				return asset
			}),"display")
		},
		customerAssetCategoryOptions() {
			return this.optionify(this.customerAssetCategories,"name")
		},
		editedMaterialFieldValues() {

			let map = {
				description: "Description",
				part_number: "Part Number",
				supplier_number: "Supplier PN",
				supplierName: "Supplier",
				manufacturer: "Manufacturer"
			}

			let fieldsValues = []

			if (this.materialFieldEditModal.record) {
				Object.keys(map).forEach(key => {
					if (key in this.materialFieldEditModal.record)
						fieldsValues.push({fieldLabel: map[key], fieldValue: this.materialFieldEditModal.record[key]})
				})
			}

			return fieldsValues
		},
		serviceQuote() {
			if ("ServiceInvoice" in this.$store.service && this.$store.service.ServiceInvoice) 
				return this.$store.service.ServiceInvoice.find(inv => inv.is_quote == 1)
			
			return null
		},
		headerButtonIcon() {
			if (this.collapsed)
				return "fa-chevron-down"
			else {
				if (this.editMode)
					return "fa-times"
				else
					return "fa-chevron-up"
			}
		},
		otherHeaderButtonIcon() {
			if (this.readonly)
				return ""
			else if (this.editMode)
				return "fa-times"
			else
				return "fa-pencil-alt"
		},
		entryTitle() {
			let title = "Work Completed"

			if ("created_at" in this.entry) {
				let date = this.moment.utc(this.entry.created_at).local().format("MMM DD")

				title += " "+date
			}

			if (this.localEntry.customer_asset_id > 0)
				title += ": Asset "+this.selectedAssetText

			return title
		},
		mutatedEquipmentUsed() {
			let vm = this

			let equipmentUsed = this.cloneObj(this.equipmentUsed)

			return equipmentUsed.map(eq => {
				eq.qtyWithUnit = eq.qty+" "+eq.qty_unit

				return eq
			})
		},
		mutatedForms() {
			let vm = this
			return this.forms.map(form => {

				form.breadcrumb = form.FormChildTemplate.FormTemplate.title
				form.childTemplateDescription = form.FormChildTemplate.description
				form.completedAt = vm.formatDateTimeToLocal(form.created_at)

				return form
			})
		}
	},
	methods: {
		importAssetForms() {
			this.assetFormsImported = true
			this.$emit('addForms', this.selectedAsset)
		},
		importAssetChecklists() {
			this.assetChecklistsImported = true
			this.$emit('addChecklists', this.selectedAsset)
		},
		selectedAssetChange() {
			this.assetChecklistsImported = false
			this.assetFormsImported = false
		},
		newMaterial() {
			this.materialModalVisible = true
		},
		editEntry() {
			this.editMode = true
			this.collapsed = false

			if ("id" in this.localEntry)
				this.fetchEntryForms()

			if (this.localEntry.customer_asset_id > 0)
				this.workedOnAsset = true
		},
		emitState() {
			this.$emit("entryChanged",{
				work_completed_description: this.localEntry.work_completed_description,
				materials: this.materialsUsed,
				equipment: this.equipmentUsed,
				tasks: this.tasksDone
			})
		},
		setLocalEntry() {
			this.localEntry = this.cloneObj(this.entry)

			this.materialsUsed = this.isValidJSON(this.localEntry.parts_used_json) ? JSON.parse(this.localEntry.parts_used_json) : []
			this.equipmentUsed = this.isValidJSON(this.localEntry.equipment_used_json) ? JSON.parse(this.localEntry.equipment_used_json) : []
			this.tasksDone = this.isValidJSON(this.localEntry.tasks_completed_json) ? JSON.parse(this.localEntry.tasks_completed_json) : []

			this.serviceTasksAdded = this.isValidJSON(this.localEntry.service_tasks_completed_json) ? JSON.parse(this.localEntry.service_tasks_completed_json) : []

			this.assetChecklistsImported = false
			this.assetFormsImported = false
		},
		headerButtonClick() {
			if (this.collapsed) {
				this.collapsed = false

				if ("id" in this.localEntry)
					this.fetchEntryForms()
			}
			else {
				if (this.editMode)
					this.editMode = false
				else
					this.collapsed = true
			}
		},
		setSelectedEquipment(eq) {
			//find category and attach to equipmne
			let vm = this

			this.getJSON("EquipmentType/find/"+eq.type_id).then(response => {
				if (response.success)
					eq.EquipmentType = response.data

				vm.equipmentModal.selectedEquipment = eq
			})
		},
		deleteEntry() {
			this.deleteEntryModalVisible = true
		},
		confirmDeleteEntry() {
			let vm = this

			if ("id" in this.entry) {

				this.postData(vm.worksheetApiResource + "/delete",{id: this.entry.id}).then(response => {
					if (response.success)
						vm.$emit("entryDeleted")
				})
			}
			else 
				this.$emit("removeEntry")

			this.deleteEntryModalVisible = false
			
		},
		moveEntry() {
			let vm = this
			if (!("id" in this.localEntry) || !this.localEntry.id) {
				this.alertFail("Save this entry before moving it to another service.")
				return
			}
			if (!this.$store.service || !this.$store.service.customer_id) {
				this.alertFail("This service has no customer set.")
				return
			}
			vm.moveEntryModal.visible = true
			vm.moveEntryModal.selectedServiceId = null
			vm.moveEntryModal.services = []
			vm.moveEntryModal.loading = true
			let completedStatuses = vm.getCompleteStatuses() || []
			let filters = [
				["customer_id", this.$store.service.customer_id],
				["archived", 0],
				["deleted", 0]
			]
			let params = {
				filters,
				fields: ["id", "slug", "job_num", "job_name", "status"]
			}
			vm.getJSON("Service/get", params).then(response => {
				vm.moveEntryModal.loading = false
				if (response.success && response.data && response.data.length) {
					let list = response.data.filter(s => s.id !== vm.serviceId)
					// Exclude completed/invoiced statuses (un-completed only)
					if (completedStatuses.length)
						list = list.filter(s => !completedStatuses.includes(s.status))
					vm.moveEntryModal.services = list
				}
			}).catch(() => {
				vm.moveEntryModal.loading = false
			})
		},
		closeMoveEntryModal() {
			this.moveEntryModal.visible = false
			this.moveEntryModal.selectedServiceId = null
			this.moveEntryModal.services = []
		},
		confirmMoveEntry() {
			let vm = this
			if (!vm.moveEntryModal.selectedServiceId || vm.moveEntryModal.working) return
			vm.moveEntryModal.working = true
			vm.postData(vm.worksheetApiResource + "/save", {
				id: vm.localEntry.id,
				service_id: vm.moveEntryModal.selectedServiceId
			}).then(response => {
				vm.moveEntryModal.working = false
				if (response.success) {
					vm.closeMoveEntryModal()
					vm.alertSaved("Entry moved to the selected service.")
					vm.$emit("entryDeleted")
				} else {
					vm.alertFail(response.message || "Failed to move entry.")
				}
			}).catch(() => {
				vm.moveEntryModal.working = false
				vm.alertFail("Failed to move entry.")
			})
		},
		addMaterialsUsed() {
			this.materialModalVisible = true
			this.$emit("addingMaterialsUsed")
		},
		hasWarrantyOrSerialNumSet(material) {
			if ("warrantyExpiry" in material && material.warrantyExpiry)
				return true
			else if ("serialNum" in material && material.serialNum)
				return true

			return false
		},
		addMaterials(payload) {
			let vm  =this

			payload.forEach(mat => {
				if (+mat.qty > 0) {
					//find a match, and add to qty, otherwise push
					let matchingIndex = vm.materialsUsed.findIndex(matUsed => matUsed.id == mat.id)

					if (matchingIndex >= 0 && !vm.hasWarrantyOrSerialNumSet(vm.materialsUsed[matchingIndex])) {
						vm.materialsUsed[matchingIndex].qty = +vm.materialsUsed[matchingIndex].qty

						vm.materialsUsed[matchingIndex].qty += +mat.qty
					}
					else
						vm.materialsUsed.push(vm.cloneObj(mat))
				}
			})
			
			this.materialModalVisible = false

			this.saveEntry(false)

			this.emitState()
		},
		removeMaterial(material, index) {
			if ("id" in material) {
				this.materialsUsed = this.materialsUsed.filter((mat,listIndex) => {

					if (mat.id != material.id || listIndex != index)
						return true
					else
						return false
					
				})
			}
			else 
				//remove by index (less reliable but required if material was added manually (meaning it has no id))
				this.materialsUsed.splice(index,1)

			this.emitState()
		},
		editMaterialQty(record) {
			let vm = this

			let params = {
				include: ["Contact"]
			}
			this.getJSON("Material/find/" + record.id,params).then(response => {

				vm.materialFieldEditModal.visible = true
				vm.materialFieldEditModal.fieldLabel = "Qty"
				vm.materialFieldEditModal.fieldType = "num"
				vm.materialFieldEditModal.fieldKey = "qty"
				vm.materialFieldEditModal.fieldValue = record.qty
				vm.materialFieldEditModal.record = record

				if (response.success) {
					if ("Supplier" in response.data && response.data.Supplier)
						vm.materialFieldEditModal.record.supplierName = response.data.Supplier.name

					if (!("manufacturer" in vm.materialFieldEditModal.record))
						vm.materialFieldEditModal.record.manufacturer = response.data.manufacturer
				}
			})
		},
		addMaterialSerialNum(record) {
			this.materialFieldEditModal.visible = true
			this.materialFieldEditModal.fieldLabel = "Serial #"
			this.materialFieldEditModal.fieldType = "text"
			this.materialFieldEditModal.fieldKey = "serialNum"
			this.materialFieldEditModal.fieldValue = record.serialNum
			this.materialFieldEditModal.record = record
		},
		addMaterialWarrantyExpiry(record) {
			this.materialFieldEditModal.visible = true
			this.materialFieldEditModal.fieldLabel = "Warranty Expiry"
			this.materialFieldEditModal.fieldType = "date"
			this.materialFieldEditModal.fieldKey = "warrantyExpiry"
			this.materialFieldEditModal.fieldValue = record.warrantyExpiry
			this.materialFieldEditModal.record = record
		},
		findMatchingMaterialIndex(material) {
			let vm = this
			return this.materialsUsed.findIndex(listMaterial => {
				if ("id" in listMaterial && "id" in material && listMaterial.id == material.id) {
					let match = true
					if ("warrantyExpiry" in listMaterial && "warrantyExpiry" in material && material.warrantyExpiry != listMaterial.warrantyExpiry)
						match = false
					if ("serialNum" in listMaterial && "serialNum" in material && material.serialNum != listMaterial.serialNum)
						match = false

					return match
				}
				return false
			})
		},
		saveMaterialEdit() {
			let vm = this

			//find material in list
			let index = this.findMatchingMaterialIndex(this.materialFieldEditModal.record)

			if (index >= 0) {

				this.materialsUsed[index][this.materialFieldEditModal.fieldKey] = this.materialFieldEditModal.fieldValue

				let payload = {
					id: this.localEntry.id,
					parts_used_json: JSON.stringify(this.materialsUsed)
				}

				this.postData(this.worksheetApiResource + "/save",payload,true).then(response => {
					if (response.success)
						vm.materialFieldEditModal.visible = false
				})
			}

			/*
			if (index >= 0) {

				if (this.materialsUsed[index].qty == 1) //only qty of 1, so modify in place
					this.materialsUsed[index][this.materialFieldEditModal.fieldKey] = this.materialFieldEditModal.fieldValue
				else {
					//qty > 1, so we need to split this into a separate line
					this.materialsUsed[index].qty--

					let cloned = JSON.parse(JSON.stringify(this.materialsUsed[index]))

					cloned.qty = 1
					cloned[this.materialFieldEditModal.fieldKey] = this.materialFieldEditModal.fieldValue

					this.materialsUsed.push(cloned)
				}

				let payload = {
					id: this.localEntry.id,
					parts_used_json: JSON.stringify(this.materialsUsed)
				}

				this.postData(vm.worksheetApiResource + "/save",payload,true).then(response => {
					if (response.success)
						vm.materialFieldEditModal.visible = false
				})
			}
			*/
		},
		addEquipment() {
			let vm = this

			let doSave = true
			if (this.equipmentModal.new) {
				
				//find description and number
				
				let eqToPush = {}
				if (this.equipmentModal.manualEntry) {
					eqToPush = {
						qty: vm.equipmentModal.payload.qty,
						qty_unit: "hours",
						description: vm.equipmentModal.payload.manualEquipment
					}
				}
				else {
					eqToPush = vm.cloneObj(this.equipmentModal.selectedEquipment)

					if (!eqToPush) {
						doSave = false
						this.alertFail("You have no equiment selected!")
					}

					else {
						eqToPush.qty = vm.equipmentModal.payload.qty
						eqToPush.qty_unit = "hours"
						eqToPush.task_id = vm.equipmentModal.payload.task_id
						eqToPush.equipmentType = "EquipmentType" in eqToPush && eqToPush.EquipmentType ? eqToPush.EquipmentType.name : ""
					}
				}
				
				if (doSave) {
					vm.equipmentUsed.push(eqToPush)
					this.saveEntry(false)
				}
			}
			else {
				//find index of edited material
				let index = this.equipmentUsed.findIndex(eq => eq.id == this.equipmentModal.payload.equipment_id)

				if (index >= 0) {
					this.equipmentUsed[index].qty = this.equipmentModal.payload.qty
					this.equipmentUsed[index].qty_unit = this.equipmentModal.payload.qty_unit
				}
			}

			if (doSave) {
				this.equipmentModal.visible = false

				this.emitState()
			}
		},
		newEquipment() {
			let vm = this

			this.equipmentModal.visible = true
			this.equipmentModal.title = "Add Equipment"
			this.equipmentModal.buttonText = "Add"
			this.equipmentModal.new = true
			this.equipmentModal.selectedEquipment = null
			this.equipmentModal.payload = {
				qty: 1,
				equipment_id: null,
				qty_unit: "hours"
			}

			let promises = []
			if (this.$store.equipmentTypes.length == 0)
				promises.push(this.fetchEquipmentTypes())

			if (this.$store.equipments.length == 0)
				promises.push(this.fetchEquipment())

			if (promises.length > 0)
				this.spinner(true)

			Promise.all(promises).then(responses => {
				if (vm.$store.equipments.length == 0)
					vm.equipmentModal.manualEntry = true

				vm.spinner(false)
			})

		},
		editEquipment(equipment) {
			this.equipmentModal.visible = true
			this.equipmentModal.title = "Edit Equipment"
			this.equipmentModal.buttonText = "Accept"
			this.equipmentModal.new = false
			this.equipmentModal.payload = {
				equipment_id: equipment.id
			}

			let fieldsToSet = ["qty","qty_unit","hrs"]
			fieldsToSet.forEach(field => {
				if (field in equipment)
					this.equipmentModal.payload[field] = equipment[field]
			})

			if (this.$store.equipmentTypes.length == 0)
				this.fetchEquipmentTypes()

			if (this.$store.equipments.length == 0)
				this.fetchEquipment()
		},
		removeEquipment(equipment) {
			let vm = this

			vm.equipmentUsed = vm.equipmentUsed.filter(eq => {
				if (eq.id == equipment.id)
					return false
				else
					return true
			})

			this.saveEntry()
		},
		newTask() {
			this.taskModal.visible = true
			this.taskModal.title = "Add Task from Schedule of Values"
			this.taskModal.buttonText = "Add"
			this.taskModal.new = true
			this.taskModal.pickFromQuote = false
			this.taskModal.tasksPayload = [{
				qty: 1,
				task_id: null,
				description: "",
				item_number: ""
			}]
			this.taskModal.taskType = "Contract Task"

			if (this.serviceQuote && !this.tasksFetched) {
				this.fetchQuoteTasks()
				if (this.taskModal.pickFromQuote === null)
					this.taskModal.pickFromQuote = true
			}
		},
		newServiceTask() {
			this.taskModal.visible = true
			this.taskModal.title = "Add Service Task"
			this.taskModal.buttonText = "Add"
			this.taskModal.new = true
			this.taskModal.taskType = "Service Task"
			this.taskModal.pickFromQuote = false
			this.taskModal.tasksPayload = [{
				qty: 1,
				task_id: null,
				description: "",
				item_number: ""
			}]

			if (!this.serviceTasksFetched) 
				this.fetchServiceTasks()
		},
		toggleTask(task) {
			if (!("selected" in task)) 
				task.selected = true
			
			else 
				task.selected = !task.selected
			
			this.$forceUpdate()
		},
		taskAddRow(value) {
			if (value > 0 && this.taskModal.new) {
				this.taskModal.tasksPayload.push({
					qty: 1,
					task_id: null,
					description: "",
					item_number: ""
				})	
			}
		},
		addTask() {
			let vm = this

			if (this.taskModal.new) {
				if (this.taskModal.pickFromQuote) {
					this.quoteTasks.forEach(task => {
						if (task.qty > 0 && task.selected) {
							let taskToPush = task.fieldValues
							taskToPush.qty = task.qty

							if (vm.taskModal.taskType == "Service Task")
								vm.serviceTasksAdded.push(taskToPush)
							else
								vm.tasksDone.push(taskToPush)
							
							vm.saveEntry(false)
						}
					})
				}
				else {
					if (this.taskModal.tasksPayload.length > 1 && !("id" in this.localEntry)) {
						//we need to save the entry first so we can generate an id and add it to the task payloads
						this.saveEntry(false).then(response => {
							if (response.success) {
								vm.localEntry.id = response.data.record_id

								vm.saveListOfTasks()
							}
						})
					}
					else
						this.saveListOfTasks()					
				}
			}
			else
				this.saveEditedTask()
			
			this.taskModal.visible = false
		},
		saveEditedTask() {
			let vm = this
			let edited = this.taskModal.tasksPayload[0]
			let isServiceTask = this.taskModal.taskType == "Service Task"
			let list = isServiceTask ? this.serviceTasksAdded : this.tasksDone
			let index = list.indexOf(this.taskModal.payload)

			if (index < 0 || !edited || !(edited.task_id > 0) || edited.qty == 0)
				return

			let catalog = null
			if (isServiceTask)
				catalog = this.$store.serviceTasks
			else if (this.$store.service && this.$store.service.ServiceContract)
				catalog = this.$store.service.ServiceContract.ServiceContractTask

			let source = null
			if (catalog)
				source = catalog.find(task => task.id == edited.task_id)

			let current = list[index]
			if (source && source.id != current.task_id && source.id != current.id) {
				let taskToPush = vm.cloneObj(source)
				taskToPush.qty = edited.qty
				taskToPush.task_id = edited.task_id
				list.splice(index, 1, taskToPush)
			}
			else
				current.qty = edited.qty

			this.saveEntry(false)
		},
		saveListOfTasks() {
			let vm = this

			this.taskModal.tasksPayload.forEach(taskToAdd => {
				if (taskToAdd.task_id > 0 && taskToAdd.qty != 0) {

					let taskList
					if (this.taskModal.taskType == 'Service Task')
						taskList = vm.$store.serviceTasks
					else 
						taskList = vm.$store.service.ServiceContract.ServiceContractTask
					
					taskList.forEach(task => {

						if (task.id == taskToAdd.task_id) {

							let taskToPush = vm.cloneObj(task)

							taskToPush.qty = taskToAdd.qty
							taskToPush.task_id = taskToAdd.task_id

							if (vm.taskModal.taskType == "Service Task")
								vm.serviceTasksAdded.push(taskToPush)
							else
								vm.tasksDone.push(taskToPush)

							vm.saveEntry(false)
						}
					})
				}
			})
		},
		editTask(task,taskType) {
			let isServiceTask = taskType == 'serviceTask'

			this.taskModal.buttonText = "Accept"
			this.taskModal.new = false
			this.taskModal.pickFromQuote = false
			this.taskModal.payload = task
			this.taskModal.taskType = isServiceTask ? "Service Task" : "Contract Task"
			this.taskModal.tasksPayload = [{
				qty: task.qty,
				task_id: task.task_id || task.id,
				description: task.description,
				item_number: task.item_number
			}]

			if (isServiceTask)
				this.taskModal.title = "Edit Service Task"
			else
				this.taskModal.title = "Edit Task from Schedule of Values"

			this.taskModal.visible = true

			if (isServiceTask && !this.serviceTasksFetched)
				this.fetchServiceTasks()
		},
		removeTask(task) {
			let vm = this

			//filter out the matching task
			vm.tasksDone = vm.tasksDone.filter(loopedTask => {
				if (loopedTask.id == task.id)
					return false
				else
					return true
			})
		},
		removeServiceTask(task) {
			let vm = this

			//filter out the matching task
			vm.serviceTasksAdded = vm.serviceTasksAdded.filter(loopedTask => {
				if (loopedTask.id == task.id)
					return false
				else
					return true
			})
		},
		newFile() {
			let vm = this

			this.uploadModal.visible = true

			this.uploadModal.payload = {
				caption: "",
				notes: "",
				file_id: null,
			}

			this.uploadModal[this.parentIdKey] = this.parentId

			if (this.$store.fileTags.length == 0) {
				this.getJSON("FileTag/get",{filters:[["is_for_services",1]]}).then(response => {
					if (response.success)
						vm.$store.fileTags = response.data
				})
			}
		},
		newForm(form_id=false, is_required=false) {

			let localEntryId = true
			if (("id" in this.localEntry) && this.localEntry > 0)
				//this is an existing entry that has an id, so pass it in, otherwise pass in true because an id doesn't exist yet, but we have to trigger worksheet forms filter
				localEntryId = this.localEntry.id

			let eventPayload = {
				service_worksheet_id: localEntryId,
			}

			eventPayload[this.parentIdKey] = this.parentId

			if (this.localEntry.customer_asset_id > 0)
				eventPayload.customer_asset_id = this.localEntry.customer_asset_id

			if (form_id)
				eventPayload.doChildTemplateIdList = [form_id]

			if (is_required)
				eventPayload.allowForcedSubmit = true

			this.$bus.$emit("doCompanyForm",eventPayload)
		},
		saveFile() {
			let vm = this

			let payload = this.cloneObj(this.uploadModal.payload)

			payload.FileTag = this.uploadModal.tags

			this.postData("ServiceFile/save",payload,true).then(response => {
				if (response.success) {
					vm.alertSaved("File added to Files tab!")
					this.uploadModal.visible = false
				}
			})
		},
		createAsset() {
			this.addAssetModal.visible = true
			this.addAssetModal.payload = {}

			if (this.customerAssetCategories.length == 0)
				this.fetchAssetCategories()
		},
		saveNewAsset() {
			let vm = this

			let payload = vm.cloneObj(this.addAssetModal.payload)

			payload.contact_id = this.$store.service.customer_id
			payload.location_id = this.$store.service.contact_location_id

			this.addAssetModal.working = true

			this.postData("CustomerAsset/save",payload,true).then(response => {
				if (response.success) {
					//assign to worksheet
					vm.localEntry.customer_asset_id = response.data.record_id
					vm.fetchData()

					vm.alertSaved("Customer Asset saved")
					vm.addAssetModal.visible = false
				}
				vm.addAssetModal.working = false
			})
		},
		imageUploaded(response) {
			let vm = this

			this.uploadModal.payload.file_id = response.data.id
		},
		fileSaved(data) {
			this.uploadModal.file_id = data.file_id
		},
		closeFileModal() {
			this.uploadModal.visible = false

			//delete uploaded image
			if (this.uploadModal.payload.file_id) 
				this.postData("LibraryUpload/delete",{id: this.uploadModal.payload.file_id})
		},
		workCompletedChange() {
			if ("id" in this.localEntry) //only save if already saved once
				this.debounce("saveEntry",null,10000)
		},
		onCompanyFormSaved(params) {
			let vm = this
			if ("worksheet_id" in params.payload && params.payload.worksheet_id === true) {
				//this is a worksheet form save, but we don't have a saved worksheet yet to tied it to
				this.spinner(true)
				this.saveEntry(false).then(response => {
					if (response.success) {
						//we've now saved the worksheet entry, update the worksheet_id on the CompanyForm record
						vm.postData("CompanyForm/save",{id: params.record_id,worksheet_id: response.data.record_id}).then(saveResponse => {
							if (saveResponse.success) {
								vm.fetchEntryForms(true)
							}
						})
					}
				})
			}
		},
		doAssetForm() {
			const vm = this

			// Asset must have form attached
			if (!(this.localEntry.customer_asset_id > 0))
				return


			this.getLocalEntryAsset().then(localEntryAsset => {

				// only prompt form if the asset has changed since the last worksheet status
				if (localEntryAsset &&
					'child_template_id' in localEntryAsset && localEntryAsset.child_template_id > 0 &&
					(!('CustomerAsset' in vm.entry && vm.entry.CustomerAsset) ||
						vm.entry.CustomerAsset.child_template_id != localEntryAsset.child_template_id))
				{
					vm.newForm(vm.localEntry.CustomerAsset.child_template_id, true)
				}

			})
		},
		getLocalEntryAsset() {
			const vm = this

			return new Promise(resolve => {

				if ('CustomerAsset' in vm.localEntry && vm.localEntry.CustomerAsset)
					resolve(vm.localEntry.CustomerAsset)

				else if (vm.localEntry.customer_asset_id && vm.localEntry.customer_asset_id > 0) {
					const params = {
						include: [
							{ model: 'FormChildTemplate', fields: ['id'] },
							'Checklist'
						]
					}

					vm.getJSON('CustomerAsset/find/' + vm.localEntry.customer_asset_id, params).then(response => {
						if (response.success) {
							vm.localEntry.CustomerAsset = response.data
							resolve(response.data)
						} else {
							resolve(false)
						}
					})
				} else {
					resolve(false)
				}
			})
		},
		saveEntry(closeEdit) {
			let vm = this

			console.log("saveEntry")

			let payload = {
				work_completed_description: this.localEntry.work_completed_description,
				parts_used_json: JSON.stringify(this.materialsUsed),
				equipment_used_json: JSON.stringify(this.equipmentUsed),
				tasks_completed_json: JSON.stringify(this.tasksDone),
				service_tasks_completed_json: JSON.stringify(this.serviceTasksAdded),
				work_completed_by: this.$store.userDetails.id,
				customer_asset_id: this.localEntry.customer_asset_id,
				is_change_quote_work: this.localEntry.is_change_quote_work
			}

			payload[this.parentIdKey] = this.parentId

			if (this.localEntry.customer_asset_id > 0) {
				payload.work_type = this.localEntry.work_type
				payload.nextServiceDueDate = this.localEntry.nextServiceDueDate
				payload.nextServiceDescription = this.localEntry.nextServiceDescription
			}

			if ("id" in this.localEntry)
				payload.id = this.localEntry.id

			return new Promise((resolve,reject) => {
				vm.postData(vm.worksheetApiResource + "/save",payload).then(response => {

					if (response.success) {
						vm.alertSaved("Entry saved")

						if (!("id" in payload))
							vm.localEntry.id = response.record_id

						vm.doAssetForm()

						if (closeEdit) {
							vm.editMode = false
							vm.$emit("entrySaved")
						}
							
					}

					if (vm.localEntry.customer_asset_id > 0) {
						vm.postData("CustomerAssetFile/save",{
							file_id: vm.uploadModal.file_id,
							asset_id: vm.localEntry.customer_asset_id,
							is_file: 0
						})
					}

					resolve(response)
				})
			})
		},
		fetchQuoteTasks() {
			let vm = this

			let params = {
				filters: [
					["invoice_id",this.serviceQuote.id],
					["type","Task"]
				]
			}

			this.getJSON("ServiceInvoiceObject/get",params).then(response => {
				if (response.success) {
					vm.quoteTasks = response.data.map(item => {
						item.fieldValues = JSON.parse(item.field_values_json)
						item.qty = 1
						return item
					})
					vm.tasksFetched = true
				}
			})
		},
		fetchAssetCategories() {
			let vm = this

			return new Promise((resolve,reject) => {

				vm.getJSON("CustomerAssetCategory/get").then(response => {
					if (response.success)
						vm.customerAssetCategories = response.data

					resolve(response)
				})

			})
		},
		fetchEntryForms(spinner) {
			let vm = this

			if (spinner)
				this.spinner(true)

			let formParams = {	
				filters: [
					["worksheet_id",this.localEntry.id]
				],
				include: [
					{
						model: "FormChildTemplate",
						fields: ["id","slug","description","form_template_id"],
						include: [
							{
								model: "FormTemplate",
								fields: ["id","slug","title"]
							}
						]
					}
				]
			}

			return new Promise((resolve,reject) => {

				vm.getJSON("CompanyForm/get",formParams).then(response => {
					if (spinner)
						vm.spinner(false)

					if (response.success)
						vm.forms = response.data

					resolve(response)
				})

			})
		},
		toggleAssetFilter() {
			let vm = this

			this.$nextTick(() => {
				vm.filteringToLocationAssets = !vm.filteringToLocationAssets

				vm.fetchAssets()
			})
		},
		fetchAssets() {
			let vm = this

			return new Promise((resolve,reject) => {

				let filters = []
				if (this.$store.service.customer_id > 0)
					filters.push(["contact_id",this.$store.service.customer_id])
				if (this.$store.service.contact_location_id > 0 && this.filteringToLocationAssets)
					filters.push(["location_id",this.$store.service.contact_location_id])

				if (filters.length > 0) {
					let params = {
						filters,
						include: [
							{ model: 'FormChildTemplate', fields: ['id'] },
							'Checklist'
						]
					}

					this.getJSON("CustomerAsset/get",params).then(response => {
						if (response.success)
							vm.customerAssets = response.data

						resolve(response)
					})
				}
				else {
					vm.customerAssets = []
					resolve({success: true, data: []})
				}
			})
		},
		fetchData() {
			let vm = this

			let promises = []

			promises.push(this.fetchAssets())

			if ("id" in this.localEntry) 
				promises.push(this.fetchEntryForms(false))

			Promise.all(promises).then(responses => {

				if (promises.length == 2)
					vm.forms = responses[1].data
			})
		}
	},
	created() {
		if (this.entry)
			this.setLocalEntry()

		this.fetchData()

		if (!("id" in this.entry) || this.triggerEditMode) {
			this.editMode = true
			this.collapsed = false
		}
		else
			this.editMode = false

		this.$bus.$on("companyFormSaved",this.onCompanyFormSaved)
	}

}
</script>

<style type="text/css" scoped>
	.inner-section h3 {
		text-transform: uppercase;
	}

	.additional-header-buttons {
		position: absolute;
		right: 40px;
		font-size: 16pt;
	}

	.collapsed-add-buttons {
		display: none;
	}

	@media (max-width: 575px) {
		.collapsed-add-buttons {
			display: block;
		}

		.expanded-add-buttons {
			display: none;
		}
	}
</style>